import { Alert, Col, Input, Modal, Progress, Row, Select, Space, Tag, Tooltip, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import { ButtonItem, ModalItem, SegmentedItem, TableItem } from "../../../../Components/Items";
import { mostrarResumenImportacion } from "../../../../Components/importarAsignacionesUI";
import {
  claveSerieNumero,
  nombreEmpleado,
  normalizarTexto,
} from "../../../../Components/importarAsignaciones.utils";
import {
  etiquetaReloj,
  prepararCatalogos,
} from "../ImportarAsignacionRelojes/plantillaAsignacionRelojes";
import {
  MODO_PIN_CODIGO,
  MODO_PIN_SIGUIENTE,
  PINES_OMITIDOS_DEFAULT,
  activasEnRelojes,
  armarBloques,
  parsearPinesOmitidos,
  planearAsignaciones,
  resumirPlan,
} from "./planAsignacionMasiva";

// Valor del filtro para los empleados sin departamento / sin puesto: el Select no
// se lleva bien con "" como valor de una opción.
const SIN_VALOR = "__sin__";

const labelStyle = { display: "block", fontSize: 11, color: "#888", marginBottom: 3 };

const OPERACIONES = {
  crear: { texto: "Crear", color: "green" },
  habilitar: { texto: "Habilitar", color: "blue" },
  sinCambios: { texto: "Ya asignado", color: "default" },
  error: { texto: "Error", color: "red" },
};

const ESTADO_INICIAL = {
  cargando: false,
  catalogos: null,
  relojIds: [],
  modoPin: MODO_PIN_SIGUIENTE,
  pinesOmitidosTexto: PINES_OMITIDOS_DEFAULT,
  pinesOmitidos: [],
  departamentos: [],
  puestos: [],
  busqueda: "",
  seleccionados: [],
  etapa: "seleccion",
  plan: [],
  enviando: false,
  progreso: null,
};

const codigoTexto = (empleado) =>
  empleado?.codigo === undefined || empleado?.codigo === null
    ? ""
    : String(empleado.codigo).padStart(4, "0");

class AgregarAsignacionesMasivas extends Component {
  state = { ...ESTADO_INICIAL };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({ ...ESTADO_INICIAL });
      this.consultarCatalogos();
    }
  }

  consultarCatalogos() {
    this.setState({ cargando: true });
    axios
      .post(
        "asignacionesRelojes/datosParaAsignacionMasivaRelojes",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ cargando: false, catalogos: prepararCatalogos(res.data) });
        } else {
          this.setState({ cargando: false });
          message.error(res.data.message || "No se pudieron cargar los empleados y relojes");
        }
      })
      .catch(() => {
        this.setState({ cargando: false });
        message.error("Error de conexión al cargar los empleados y relojes");
      });
  }

  cerrar() {
    // Con un envío a medias no se puede cerrar: los bloques siguientes se
    // seguirían mandando sin que nadie vea el resultado.
    if (this.state.enviando) return;
    this.props.cerrarAgregarAsignacionesMasivas();
  }

  // ─── Selección ─────────────────────────────────────────────────────────────

  empleadosActivos() {
    const { catalogos } = this.state;
    if (!catalogos) return [];
    return catalogos.empleados.filter((empleado) => Number(empleado.estado) === 1);
  }

  opcionesDe(campo, etiquetaVacia) {
    const valores = new Set(this.empleadosActivos().map((empleado) => empleado[campo] || SIN_VALOR));
    return [...valores]
      .map((valor) => ({ value: valor, label: valor === SIN_VALOR ? etiquetaVacia : valor }))
      .sort((a, b) => {
        if (a.value === SIN_VALOR) return 1;
        if (b.value === SIN_VALOR) return -1;
        return a.label.localeCompare(b.label);
      });
  }

  filasDeEmpleados() {
    const { catalogos, departamentos, puestos, busqueda, relojIds } = this.state;
    const textoBuscado = normalizarTexto(busqueda);

    return this.empleadosActivos()
      .filter(
        (empleado) =>
          departamentos.length === 0 || departamentos.includes(empleado.departamento || SIN_VALOR)
      )
      .filter((empleado) => puestos.length === 0 || puestos.includes(empleado.puesto || SIN_VALOR))
      .map((empleado) => ({
        _id: String(empleado._id),
        codigo: codigoTexto(empleado),
        numero: claveSerieNumero(empleado),
        nombre: nombreEmpleado(empleado),
        departamento: empleado.departamento || "",
        puesto: empleado.puesto || "",
        yaAsignado: relojIds.length ? activasEnRelojes(catalogos, empleado._id, relojIds) : [],
      }))
      .filter(
        (fila) =>
          !textoBuscado ||
          normalizarTexto(`${fila.nombre} ${fila.codigo} ${fila.numero}`).includes(textoBuscado)
      )
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }

  // Ya activo en todos los relojes elegidos: no hay nada que darle de alta.
  estaCompleto(fila) {
    return this.state.relojIds.length > 0 && fila.yaAsignado.length >= this.state.relojIds.length;
  }

  seleccionarFiltrados(filas) {
    const ids = new Set(this.state.seleccionados);
    for (const fila of filas) {
      if (!this.estaCompleto(fila)) ids.add(fila._id);
    }
    this.setState({ seleccionados: [...ids] });
  }

  revisar() {
    const { catalogos, seleccionados, relojIds, modoPin, pinesOmitidosTexto } = this.state;
    const { pines, error } = parsearPinesOmitidos(pinesOmitidosTexto);
    if (error) {
      message.error(`Omitir números: ${error}`);
      return;
    }
    const plan = planearAsignaciones({
      catalogos,
      empleadoIds: seleccionados,
      relojIds,
      modoPin,
      pinesOmitidos: pines,
    });
    this.setState({ etapa: "revision", plan, pinesOmitidos: pines });
  }

  renderSeleccion() {
    const {
      catalogos,
      relojIds,
      modoPin,
      pinesOmitidosTexto,
      departamentos,
      puestos,
      busqueda,
      seleccionados,
    } = this.state;

    const errorOmitidos = parsearPinesOmitidos(pinesOmitidosTexto).error;
    const filas = this.filasDeEmpleados();
    const seleccionablesFiltrados = filas.filter((fila) => !this.estaCompleto(fila)).length;

    const opcionesRelojes = (catalogos?.relojes || [])
      .filter((reloj) => Number(reloj.estado) === 1)
      .map((reloj) => ({ value: String(reloj._id), label: etiquetaReloj(reloj) }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const columnas = [
      { title: "Código", dataIndex: "codigo", key: "codigo", width: 80 },
      { title: "Número", dataIndex: "numero", key: "numero", width: 100 },
      { title: "Empleado", dataIndex: "nombre", key: "nombre" },
      { title: "Departamento", dataIndex: "departamento", key: "departamento" },
      { title: "Puesto", dataIndex: "puesto", key: "puesto" },
      {
        title: "Ya asignado en",
        dataIndex: "yaAsignado",
        key: "yaAsignado",
        render: (yaAsignado) =>
          (yaAsignado || []).map((activa) => (
            <Tooltip key={activa.relojId} title={activa.relojNombre}>
              <Tag color="green">
                {activa.relojNombreCorto} · {activa.pin}
              </Tag>
            </Tooltip>
          )),
      },
    ];

    return (
      <>
        <Row gutter={12} style={{ marginBottom: 10 }}>
          <Col xs={24} md={10}>
            <label style={labelStyle}>Relojes checadores</label>
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Elige uno o varios relojes"
              options={opcionesRelojes}
              optionFilterProp="label"
              value={relojIds}
              onChange={(valor) => this.setState({ relojIds: valor })}
            />
          </Col>
          <Col xs={24} md={10}>
            <label style={labelStyle}>Usuario Reloj</label>
            <SegmentedItem
              options={[
                { label: "Siguiente libre", value: MODO_PIN_SIGUIENTE },
                { label: "Código de empleado", value: MODO_PIN_CODIGO },
              ]}
              value={modoPin}
              onChange={(valor) => this.setState({ modoPin: valor })}
            />
            <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 4 }}>
              {modoPin === MODO_PIN_CODIGO
                ? "El usuario del reloj será el Código del empleado. Los que no tienen código no se aplican."
                : "Cada empleado recibe el siguiente número libre del reloj. Si ya tuvo una asignación deshabilitada en ese reloj, se vuelve a prender con su mismo número."}
            </div>
          </Col>
          <Col xs={24} md={4}>
            <label style={labelStyle}>Omitir números</label>
            <Input
              allowClear
              placeholder="Ej. 1, 5, 10-20"
              value={pinesOmitidosTexto}
              style={errorOmitidos ? { borderColor: "#ff4d4f" } : undefined}
              onChange={(e) => this.setState({ pinesOmitidosTexto: e.target.value })}
            />
            <div style={{ fontSize: 11, color: errorOmitidos ? "#ff4d4f" : "#8c8c8c", marginTop: 4 }}>
              {errorOmitidos || "No se le dan a nadie (el 1 es el administrador del reloj)."}
            </div>
          </Col>
        </Row>

        <Row gutter={12} style={{ marginBottom: 10 }}>
          <Col xs={24} md={8}>
            <label style={labelStyle}>Departamento</label>
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Todos"
              options={this.opcionesDe("departamento", "(Sin departamento)")}
              optionFilterProp="label"
              value={departamentos}
              onChange={(valor) => this.setState({ departamentos: valor })}
            />
          </Col>
          <Col xs={24} md={8}>
            <label style={labelStyle}>Puesto</label>
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Todos"
              options={this.opcionesDe("puesto", "(Sin puesto)")}
              optionFilterProp="label"
              value={puestos}
              onChange={(valor) => this.setState({ puestos: valor })}
            />
          </Col>
          <Col xs={24} md={8}>
            <label style={labelStyle}>Buscar</label>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Nombre, código o número"
              value={busqueda}
              onChange={(e) => this.setState({ busqueda: e.target.value })}
            />
          </Col>
        </Row>

        <Space style={{ marginBottom: 8 }} wrap>
          <ButtonItem
            type="default"
            size="small"
            disabled={seleccionablesFiltrados === 0}
            onClick={() => this.seleccionarFiltrados(filas)}
          >
            Seleccionar filtrados ({seleccionablesFiltrados})
          </ButtonItem>
          <ButtonItem
            type="default"
            size="small"
            blanco
            disabled={seleccionados.length === 0}
            onClick={() => this.setState({ seleccionados: [] })}
          >
            Limpiar selección
          </ButtonItem>
          <span style={{ fontSize: 12, color: "#595959" }}>
            {seleccionados.length} empleado(s) seleccionado(s)
          </span>
        </Space>

        <TableItem
          rowKey={(fila) => fila._id}
          columns={columnas}
          dataSource={filas}
          size="small"
          defaultPageSize={50}
          rowClassName={(_, indice) => (indice % 2 === 0 ? "zebra-gris" : "default")}
          rowSelection={{
            selectedRowKeys: seleccionados,
            // Sin esto antd suelta de la selección a quien deja de verse al cambiar
            // un filtro, y la selección no se podría armar por partes.
            preserveSelectedRowKeys: true,
            onChange: (keys) => this.setState({ seleccionados: keys }),
            getCheckboxProps: (fila) => ({ disabled: this.estaCompleto(fila) }),
          }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <ButtonItem
            type="primary"
            disabled={relojIds.length === 0 || seleccionados.length === 0 || !!errorOmitidos}
            onClick={() => this.revisar()}
          >
            Revisar ({seleccionados.length} empleado(s) × {relojIds.length} reloj(es))
          </ButtonItem>
        </div>
      </>
    );
  }

  // ─── Revisión y envío ──────────────────────────────────────────────────────

  confirmarAplicar(resumen) {
    Modal.confirm({
      title: "Aplicar asignaciones de reloj",
      content: (
        <div>
          <p>
            Se van a dar de alta <b>{resumen.crear}</b> asignación(es) nueva(s)
            {resumen.habilitar ? (
              <>
                {" "}
                y a volver a habilitar <b>{resumen.habilitar}</b>
              </>
            ) : null}
            .
          </p>
          <p style={{ marginBottom: 0 }}>
            El alta en el reloj físico <b>no es instantánea</b>: se encola y el dispositivo se la
            lleva poco a poco cada vez que se conecta. Revisa cómo va en Relojes Checadores →
            Información del Reloj Checador.
          </p>
        </div>
      ),
      okText: "Sí, aplicar",
      cancelText: "Cancelar",
      // Sin regresar la promesa: antd dejaría el confirm abierto durante todo el
      // envío, tapando la barra de progreso.
      onOk: () => {
        this.aplicar();
      },
    });
  }

  // Bloques en serie, igual que el importador Excel: cada alta encola varias
  // AccionReloj y lee al empleado completo, así que no va todo en una petición.
  async aplicar() {
    // Dos confirms abiertos con doble clic no deben mandar el lote dos veces.
    if (this.enviandoLote) return;
    this.enviandoLote = true;

    const bloques = armarBloques(this.state.plan);
    const total = bloques.reduce((suma, b) => suma + b.crear.length + b.editar.length, 0);

    const agregado = {
      numeroCreados: 0,
      numeroEditados: 0,
      resultados: [],
      procesadas: 0,
      totalFilas: total,
    };

    this.setState({ enviando: true, progreso: { hechas: 0, total } });

    for (const bloque of bloques) {
      let res;
      try {
        res = await axios.post(
          "asignacionesRelojes/importarAsignacionesRelojes",
          // pinesOmitidos en cada bloque: el backend arma el "siguiente libre" de
          // nuevo en cada petición.
          { empresaId: this.props.empresaId, ...bloque, pinesOmitidos: this.state.pinesOmitidos },
          { headers: this.props.headersToken }
        );
      } catch (err) {
        agregado.incompleto = true;
        agregado.mensajeError = err.response?.data?.message
          ? err.response.data.message
          : err.response?.status
            ? `El servidor respondió ${err.response.status}`
            : `Error de red: ${err.message}`;
        break;
      }

      if (res.data.success !== 2) {
        agregado.incompleto = true;
        agregado.mensajeError = res.data.message || "El servidor no pudo aplicar las asignaciones";
        break;
      }

      agregado.numeroCreados += res.data.numeroCreados ?? 0;
      agregado.numeroEditados += res.data.numeroEditados ?? 0;
      if (Array.isArray(res.data.resultados)) agregado.resultados.push(...res.data.resultados);
      agregado.procesadas += bloque.crear.length + bloque.editar.length;

      this.setState({ progreso: { hechas: agregado.procesadas, total } });
    }

    this.enviandoLote = false;
    this.setState({ enviando: false, progreso: null });

    if (agregado.numeroCreados + agregado.numeroEditados > 0) {
      message.success(
        `Se dieron de alta ${agregado.numeroCreados} y se volvieron a habilitar ${agregado.numeroEditados} asignaciones de reloj`
      );
    }

    if (agregado.incompleto) {
      message.error(
        `${agregado.mensajeError}. Se procesaron ${agregado.procesadas} de ${total}. Vuelve a abrir "Agregar Varias": los que ya quedaron aparecen como asignados.`,
        10
      );
    }

    // Sólo el detalle de los renglones que el servidor rechazó: el aviso de envío
    // incompleto de ese resumen habla de la plantilla Excel, y aquí no aplica.
    mostrarResumenImportacion(
      { ...agregado, incompleto: false },
      { entidad: "asignaciones de reloj" }
    );

    this.props.cerrarAgregarAsignacionesMasivas();
  }

  renderRevision() {
    const { plan, enviando, progreso, pinesOmitidosTexto, pinesOmitidos } = this.state;
    const resumen = resumirPlan(plan);
    const porAplicar = resumen.crear + resumen.habilitar;

    const columnas = [
      {
        title: "Operación",
        dataIndex: "operacion",
        key: "operacion",
        width: 110,
        render: (valor) => {
          const operacion = OPERACIONES[valor];
          return operacion ? <Tag color={operacion.color}>{operacion.texto}</Tag> : valor;
        },
      },
      { title: "Empleado", dataIndex: "empleadoNombre", key: "empleadoNombre" },
      { title: "Reloj Checador", dataIndex: "relojChecadorNombre", key: "relojChecadorNombre" },
      {
        title: "Usuario Reloj",
        dataIndex: "usuarioRelojChecador",
        key: "usuarioRelojChecador",
        width: 130,
        render: (valor, fila) =>
          valor ? (
            valor
          ) : fila.operacion === "crear" ? (
            <span style={{ color: "#8c8c8c" }}>(siguiente libre)</span>
          ) : (
            ""
          ),
      },
      { title: "Detalle", dataIndex: "motivo", key: "motivo" },
    ];

    return (
      <>
        <Alert
          type={resumen.error ? "warning" : "info"}
          showIcon
          style={{ marginBottom: 10 }}
          message={
            <span>
              <b>{resumen.crear}</b> alta(s) nueva(s) · <b>{resumen.habilitar}</b> por volver a
              habilitar · <b>{resumen.sinCambios}</b> ya asignada(s), se omiten ·{" "}
              <b>{resumen.error}</b> con error, no se aplican
            </span>
          }
          description={
            pinesOmitidos.length
              ? `No se le da a nadie el usuario: ${pinesOmitidosTexto.trim()}`
              : undefined
          }
        />

        {progreso && (
          <Progress
            percent={progreso.total ? Math.round((progreso.hechas / progreso.total) * 100) : 0}
            format={() => `${progreso.hechas} / ${progreso.total}`}
            style={{ marginBottom: 10 }}
          />
        )}

        <TableItem
          rowKey={(fila) => fila.key}
          columns={columnas}
          dataSource={plan}
          size="small"
          defaultPageSize={50}
          rowClassName={(_, indice) => (indice % 2 === 0 ? "zebra-gris" : "default")}
        />

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
          <ButtonItem
            type="default"
            blanco
            disabled={enviando}
            onClick={() => this.setState({ etapa: "seleccion", plan: [] })}
          >
            Atrás
          </ButtonItem>
          <ButtonItem
            type="primary"
            loading={enviando}
            disabled={porAplicar === 0 || enviando}
            onClick={() => this.confirmarAplicar(resumen)}
          >
            Aplicar ({porAplicar})
          </ButtonItem>
        </div>
      </>
    );
  }

  render() {
    return (
      <ModalItem
        title="Agregar Varias Asignaciones de Reloj Checador"
        visible={this.props.visible}
        spinning={this.state.cargando}
        onCancel={() => this.cerrar()}
        closable={!this.state.enviando}
        maskClosable={false}
        width={window.innerWidth > 1000 ? "80%" : "95%"}
        paddingTop
      >
        {this.state.etapa === "revision" ? this.renderRevision() : this.renderSeleccion()}
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    headersToken: state.user.headersToken,
    empresaId: state.empresa.id,
  };
};

export default connect(mapStateToProps)(AgregarAsignacionesMasivas);
